import { useCallback, useRef, useState } from 'react';

import { Drawer } from '../../../../components/Drawer';
import Dropdown from '../../../../components/Dropdown';
import { FormList, FormListFieldError, FormListItem } from '../../../../components/FormList';
import { HelpDrawerLink } from '../../../../components/HelpDrawerLink';
import TextField from '../../../../components/TextField';
import { WorkflowSubStepProps } from '../../config';

export interface RelativeEntry {
  relativeDetails: string;
}

export interface RelativesFormData {
  isAddingEntry?: boolean;
  hasRelatives: boolean | null;
  relatives: FormListItem<RelativeEntry>[];
  draftRelative: RelativeEntry;
}

const yesNoOptions = [
  { label: '- Select an option -', value: null },
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

const getEmptyRelative = (): RelativeEntry => ({
  relativeDetails: '',
});

function RelativesPage({ data, fieldErrors, updateData }: WorkflowSubStepProps<RelativesFormData>) {
  const [isHelpDrawerOpen, setIsHelpDrawerOpen] = useState(false);
  const baseTestId = 'relatives-page';

  const handleRelativesChange = (value: boolean | null) => {
    if (value !== true) {
      updateData({
        ...data,
        draftRelative: getEmptyRelative(),
        hasRelatives: value,
        isAddingEntry: false,
        relatives: [],
      });
    } else {
      updateData({
        ...data,
        hasRelatives: value,
      });
    }
  };

  const pendingItemsRef = useRef<FormListItem<RelativeEntry>[] | null>(null);

  const handleItemsChange = useCallback(
    (items: FormListItem<RelativeEntry>[]) => {
      pendingItemsRef.current = items;
      updateData({
        ...data,
        relatives: items,
      });
    },
    [data, updateData],
  );

  const handleDraftChange = useCallback(
    (draft: RelativeEntry, isDraftActive?: boolean) => {
      const itemsToUse = pendingItemsRef.current !== null ? pendingItemsRef.current : data.relatives;
      pendingItemsRef.current = null;

      updateData({
        ...data,
        draftRelative: draft,
        isAddingEntry: isDraftActive ?? data.isAddingEntry,
        relatives: itemsToUse,
      });
    },
    [data, updateData],
  );

  const handleDraftActiveChange = useCallback(
    (isActive: boolean) => updateData({ ...data, isAddingEntry: isActive }),
    [data, updateData],
  );

  const validateRelative = useCallback((item: RelativeEntry): FormListFieldError => {
    const errors: FormListFieldError = {};

    if (!item.relativeDetails) {
      errors.relativeDetails = 'Details about the relative are required';
    }

    return errors;
  }, []);

  const isDraftEmpty = useCallback((draft: RelativeEntry): boolean => {
    return !draft.relativeDetails;
  }, []);

  const renderEditForm = useCallback(
    (item: RelativeEntry, onChange: (item: RelativeEntry) => void, errors: FormListFieldError) => {
      const handleFieldChange = (value: string) => {
        onChange({
          ...item,
          relativeDetails: value,
        });
      };

      return (
        <div
          className='form-list__nested-form'
          data-testid={`${baseTestId}-entry-form`}>
          <div
            className='ds-u-margin-bottom--3'
            data-testid={`${baseTestId}-entry-details`}>
            <TextField
              multiline
              required
              data-testid={`${baseTestId}-entry-details-field`}
              errorMessage={errors.relativeDetails}
              label='Details about the relative'
              name='relativeDetails'
              rows={5}
              tooltipText="Provide details including the relative's name, relationship, and the department, agency, or branch of the Armed Forces for which your relative works"
              value={item.relativeDetails || ''}
              onChange={(e) => handleFieldChange(e.target.value)}
            />
          </div>
        </div>
      );
    },
    [],
  );

  const renderItemSummary = useCallback((item: RelativeEntry) => {
    const truncatedDetails =
      item.relativeDetails.length > 50 ? `${item.relativeDetails.substring(0, 50)}...` : item.relativeDetails;

    return {
      details: '',
      title: truncatedDetails,
    };
  }, []);

  const renderReadOnlyItem = useCallback((item: RelativeEntry) => {
    return (
      <div className='form-list__nested-form'>
        <div className='form-list__readonly-field'>
          <span className='form-list__readonly-label'>Details</span>
          <span className='form-list__readonly-value'>{item.relativeDetails}</span>
        </div>
      </div>
    );
  }, []);

  const draftFieldErrors: FormListFieldError = {
    relativeDetails: fieldErrors?.['draftRelative.relativeDetails'],
  };

  return (
    <div data-testid={baseTestId}>
      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-has-relatives`}>
        <Dropdown
          required
          data-testid={`${baseTestId}-has-relatives-dropdown`}
          errorMessage={fieldErrors?.hasRelatives}
          label='Do any of your relatives work for the agency or government organization to which you are submitting this form?'
          name='hasRelatives'
          options={yesNoOptions}
          tooltipText='This question helps identify potential conflicts of interest.'
          value={data.hasRelatives}
          onChange={handleRelativesChange}
        />
      </div>

      {data.hasRelatives === true && (
        <div
          className='ds-u-margin-bottom--3'
          data-testid={`${baseTestId}-entries`}>
          <FormList<RelativeEntry>
            addLinkText='Add Another Relative'
            data-testid={`${baseTestId}-entries-list`}
            draftItem={data.draftRelative}
            fieldErrors={draftFieldErrors}
            getEmptyItem={getEmptyRelative}
            isDraftActive={data.isAddingEntry}
            isDraftEmpty={isDraftEmpty}
            items={data.relatives}
            label='Details about the relative(s)'
            renderEditForm={renderEditForm}
            renderItemSummary={renderItemSummary}
            renderReadOnlyItem={renderReadOnlyItem}
            tooltipText='Provide details about each relative who works for the agency.'
            validateItem={validateRelative}
            onDraftActiveChange={handleDraftActiveChange}
            onDraftChange={handleDraftChange}
            onItemsChange={handleItemsChange}
          />
        </div>
      )}

      <div
        className='ds-u-border-top--2 ds-u-padding-top--3 ds-u-margin-top--4 field-max-width'
        data-testid={`${baseTestId}-help`}>
        <HelpDrawerLink
          data-testid={`${baseTestId}-help-link`}
          onClick={() => setIsHelpDrawerOpen(true)}
        />
      </div>

      <Drawer
        data-testid={`${baseTestId}-drawer`}
        heading='Help: Relatives in Government'
        isOpen={isHelpDrawerOpen}
        onClose={() => setIsHelpDrawerOpen(false)}>
        <div className='ds-u-padding--3'>
          <h3 className='ds-u-margin-top--0'>Get Help with Relatives in Government</h3>
          <p>
            This section asks if any of your family members work for the agency or government organization you&apos;re
            applying to. This is required for the Declaration of Federal Employment (OF-306).
          </p>

          <h4 className='ds-u-margin-top--3'>Why is this information needed?</h4>
          <p>
            This information helps us ensure we comply with Federal Nepotism Laws. These laws are a set of strict rules
            that must be followed. They help keep public officials from hiring, promoting, or advocating for their own
            family members within the government.
          </p>
          <h4 className='ds-u-margin-top--3'>A relative can be:</h4>
          <ul>
            <li>Spouse, parents, and children</li>
            <li>Brothers, sisters, and in-laws</li>
            <li>Aunts, uncles, and cousins</li>
            <li>Step- and half-relatives</li>
          </ul>

          {/* TODO: Restore the following guidance when ACP-793 is completed with these fields.
          <h4 className='ds-u-margin-top--3'>For each family member:</h4>
          <ul>
            <li>Enter their name.</li>
            <li>Enter how this person is related to you.</li>
            <li>Enter the department, agency, or branch of the Armed Forces where they work.</li>
          </ul>
          */}
          <p className='ds-u-margin-top--3'>
            <strong>Note:</strong> If you have more than one relative to detail, use the &quot;Add Another
            Relative&quot; button to add each one.
          </p>
        </div>
      </Drawer>
    </div>
  );
}

export default RelativesPage;
